import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  CircularProgress,
  Chip,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CustomTextField from "../../../../../../components/ui/Textfield";
import PhoneNumberField from "../../../../../../components/ui/PhoneNumberField";
import apiService from "../../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";

interface AddStreamContactModalProps {
  open: boolean;
  onClose: () => void;
  streamId: number | null;
  onSuccess: () => void;
}

const RELATIONSHIP_SUGGESTIONS = [
  "Family",
  "Close Friend",
  "Colleague",
  "Church Member",
  "Relative",
  "In-law",
];

export const AddStreamContactModal: React.FC<AddStreamContactModalProps> = ({
  open,
  onClose,
  streamId,
  onSuccess,
}) => {

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [notes, setNotes] = useState("");

  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setPhone("");
    setEmail("");
    setRole("");
    setNotes("");
    setFirstNameError("");
    setLastNameError("");
    setPhoneError("");
    setEmailError("");
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  const validate = (): boolean => {
    let isValid = true;

    if (!firstName.trim()) {
      setFirstNameError("First name is required.");
      isValid = false;
    } else {
      setFirstNameError("");
    }

    if (!lastName.trim()) {
      setLastNameError("Last name is required.");
      isValid = false;
    } else {
      setLastNameError("");
    }

    const cleanPhone = phone.trim().replace(/\s+/g, "");
    if (!cleanPhone && !email.trim()) {
      setPhoneError("Provide a phone number or email (phone required for WhatsApp).");
      isValid = false;
    } else {
      setPhoneError("");
    }

    if (email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        setEmailError("Please enter a valid email address.");
        isValid = false;
      } else {
        setEmailError("");
      }
    } else {
      setEmailError("");
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    const cleanPhone = phone.trim().replace(/\s+/g, "");

    const payload = {
      contact_type: "individual",
      contact_source: "mamorial_page",
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      phone: cleanPhone || "N/A",
      email: email.trim() || null,
      role: role.trim() || null,
      notes: notes.trim() || null,
      from_stream: streamId,
    };

    try {
      try {
        await apiService.post("/api/contacts/contact/", payload);
        showSuccessToast(
          `Contact "${firstName.trim()} ${lastName.trim()}" added successfully.`
        );
        resetForm();
        onSuccess();
        onClose();
        return;
      } catch (err: unknown) {
        const errorObj = err as {
          response?: {
            data?: {
              phone?: string[];
              email?: string[];
              non_field_errors?: string[];
              detail?: string;
            };
          };
        };

        const phoneErr = errorObj.response?.data?.phone?.[0] || "";
        const emailErr = errorObj.response?.data?.email?.[0] || "";
        const nonFieldErr = errorObj.response?.data?.non_field_errors?.[0] || "";
        const detailErr = errorObj.response?.data?.detail || "";

        const isDuplicate =
          phoneErr.toLowerCase().includes("already exists") ||
          emailErr.toLowerCase().includes("already exists") ||
          nonFieldErr.toLowerCase().includes("already exists") ||
          detailErr.toLowerCase().includes("already exists");

        if (isDuplicate) {
          // Check if contact can be linked to this stream or updated
          const searchVal = cleanPhone || email.trim();
          const searchRes = await apiService.get<{
            results: Array<{
              id: number;
              from_stream?: number | null;
              from_stream_id?: number | null;
              first_name?: string;
              last_name?: string;
            }>;
          }>(`/api/contacts/contact/?search=${encodeURIComponent(searchVal)}`);

          const matched = searchRes.data?.results?.[0];
          if (matched) {
            const currentStream = matched.from_stream || matched.from_stream_id;
            if (currentStream === streamId) {
              showErrorToast(
                `A contact with this ${
                  cleanPhone ? "phone number" : "email"
                } already exists in this memorial page.`
              );
              return;
            } else {
              // Contact exists elsewhere or without a stream: link to this stream!
              await apiService.patch(`/api/contacts/contact/${matched.id}/`, {
                from_stream: streamId,
                role: role.trim() || undefined,
                notes: notes.trim() || undefined,
              });
              showSuccessToast(
                `Existing contact "${
                  matched.first_name || firstName
                } ${matched.last_name || lastName}" linked to this memorial page successfully.`
              );
              resetForm();
              onSuccess();
              onClose();
              return;
            }
          }
        }

        const fallbackMsg =
          phoneErr ||
          emailErr ||
          nonFieldErr ||
          detailErr ||
          "Failed to add contact. Please check the entered fields.";
        showErrorToast(fallbackMsg);
      }
    } catch {
      showErrorToast("An unexpected error occurred while adding contact.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          p: 1,
          boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1.5,
            pt: 1.5,
            px: 2.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: "#f5ece8",
                color: "#614438",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PersonAddIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Add Contact
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Add a contact directly to this memorial page
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={isSubmitting}
            size="small"
            sx={{
              color: "text.secondary",
              "&:hover": { color: "#ef4444", bgcolor: "#fee2e2" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, py: 2.5 }}>
          <Stack spacing={2.5}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomTextField
                  label="First Name *"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (firstNameError) setFirstNameError("");
                  }}
                  error={Boolean(firstNameError)}
                  helperText={firstNameError}
                  fullWidth
                  autoFocus
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomTextField
                  label="Last Name *"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (lastNameError) setLastNameError("");
                  }}
                  error={Boolean(lastNameError)}
                  helperText={lastNameError}
                  fullWidth
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>

            <Box>
              <PhoneNumberField
                label="Phone Number"
                value={phone}
                onChange={(val) => {
                  setPhone(val);
                  if (phoneError) setPhoneError("");
                }}
                defaultCountry="ZW"
                fullWidth
                disabled={isSubmitting}
                error={Boolean(phoneError)}
                helperText={
                  phoneError || "Recommended for sending WhatsApp thank-you messages."
                }
              />
            </Box>

            <CustomTextField
              label="Email Address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError("");
              }}
              error={Boolean(emailError)}
              helperText={emailError || "Optional — used for notifications and records."}
              fullWidth
              type="email"
              disabled={isSubmitting}
            />

            <Box>
              <CustomTextField
                label="Relationship / Role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="e.g. Family, Close Friend, Colleague"
                fullWidth
                disabled={isSubmitting}
                sx={{ mb: 1 }}
              />
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {RELATIONSHIP_SUGGESTIONS.map((suggestion) => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    size="small"
                    onClick={() => setRole(suggestion)}
                    clickable
                    variant={role === suggestion ? "filled" : "outlined"}
                    sx={{
                      fontSize: "0.75rem",
                      bgcolor: role === suggestion ? "#614438" : "transparent",
                      color: role === suggestion ? "#ffffff" : "text.secondary",
                      borderColor: role === suggestion ? "#614438" : "divider",
                      "&:hover": {
                        bgcolor: role === suggestion ? "#4e362d" : "#f5ece8",
                        color: role === suggestion ? "#ffffff" : "#614438",
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <CustomTextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details or comments..."
              fullWidth
              multiline
              rows={2}
              disabled={isSubmitting}
            />
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              height: 38,
              px: 2.5,
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <PersonAddIcon />
              )
            }
            sx={{
              bgcolor: "#614438",
              "&:hover": { bgcolor: "#4e362d" },
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              height: 38,
              px: 3,
            }}
          >
            {isSubmitting ? "Adding..." : "Add Contact"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
